// Categorized property amenities - local storage instead of Uplisting API
// This allows full control over categorization, ordering, and adding custom amenities

export interface AmenityCategory {
  name: string;
  amenities: string[];
}

// Normalize amenity strings for robust matching
// - lowercase
// - trimmed
// - replace & with 'and', then back to consistent format
// - collapse multiple spaces
// - remove special characters except spaces
function normalizeAmenity(amenity: string): string {
  return amenity
    .toLowerCase()
    .trim()
    .replace(/&/g, 'and')
    .replace(/\//g, ' ')
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

// Map of normalized amenity names to their display name and category
// Keys are normalized: lowercase, no punctuation, & becomes 'and', / becomes space
const amenityMapping: Record<string, { display: string; category: string }> = {
  // Kitchen & Dining
  'kitchen': { display: 'Kitchen', category: 'Kitchen & Dining' },
  'full kitchen': { display: 'Full Kitchen', category: 'Kitchen & Dining' },
  'oven': { display: 'Oven', category: 'Kitchen & Dining' },
  'microwave': { display: 'Microwave', category: 'Kitchen & Dining' },
  'toaster': { display: 'Toaster', category: 'Kitchen & Dining' },
  'coffee maker': { display: 'Coffee Maker', category: 'Kitchen & Dining' },
  'coffee machine': { display: 'Coffee Machine', category: 'Kitchen & Dining' },
  'espresso machine': { display: 'Espresso Machine', category: 'Kitchen & Dining' },
  'dishes and utensils': { display: 'Dishes & Utensils', category: 'Kitchen & Dining' },
  'dishwasher': { display: 'Dishwasher', category: 'Kitchen & Dining' },
  'pantry items': { display: 'Pantry Items', category: 'Kitchen & Dining' },
  'refrigerator': { display: 'Refrigerator', category: 'Kitchen & Dining' },
  'fridge': { display: 'Refrigerator', category: 'Kitchen & Dining' },
  'stove': { display: 'Stove', category: 'Kitchen & Dining' },
  'hot water kettle': { display: 'Hot Water Kettle', category: 'Kitchen & Dining' },
  'wine glasses': { display: 'Wine Glasses', category: 'Kitchen & Dining' },
  'baking sheet': { display: 'Baking Sheet', category: 'Kitchen & Dining' },
  'dining table': { display: 'Dining Table', category: 'Kitchen & Dining' },
  'dining area': { display: 'Dining Area', category: 'Kitchen & Dining' },
  
  // Bathroom & Laundry
  'washing machine': { display: 'Washing Machine', category: 'Bathroom & Laundry' },
  'washer': { display: 'Washing Machine', category: 'Bathroom & Laundry' },
  'washer dryer': { display: 'Washer / Dryer', category: 'Bathroom & Laundry' },
  'washer and dryer': { display: 'Washer / Dryer', category: 'Bathroom & Laundry' },
  'clothes dryer': { display: 'Clothes Dryer', category: 'Bathroom & Laundry' },
  'dryer': { display: 'Clothes Dryer', category: 'Bathroom & Laundry' },
  'hair dryer': { display: 'Hair Dryer', category: 'Bathroom & Laundry' },
  'hairdryer': { display: 'Hair Dryer', category: 'Bathroom & Laundry' },
  'iron and board': { display: 'Iron & Board', category: 'Bathroom & Laundry' },
  'iron': { display: 'Iron', category: 'Bathroom & Laundry' },
  'shampoo': { display: 'Shampoo', category: 'Bathroom & Laundry' },
  'body soap': { display: 'Body Soap', category: 'Bathroom & Laundry' },
  'body soap shampoo and conditioner': { display: 'Body Soap, Shampoo & Conditioner', category: 'Bathroom & Laundry' },
  'hot water': { display: 'Hot Water', category: 'Bathroom & Laundry' },
  'towels provided': { display: 'Towels Provided', category: 'Bathroom & Laundry' },
  'towels': { display: 'Towels Provided', category: 'Bathroom & Laundry' },
  'cleaning products': { display: 'Cleaning Products', category: 'Bathroom & Laundry' },
  'essentials': { display: 'Essentials', category: 'Bathroom & Laundry' },
  
  // Bedroom
  'linens provided': { display: 'Linens Provided', category: 'Bedroom' },
  'bed linen': { display: 'Bed Linen', category: 'Bedroom' },
  'bed linens': { display: 'Bed Linen', category: 'Bedroom' },
  'extra pillows and blankets': { display: 'Extra Pillows & Blankets', category: 'Bedroom' },
  'extra pillows': { display: 'Extra Pillows & Blankets', category: 'Bedroom' },
  'hangers': { display: 'Hangers', category: 'Bedroom' },
  'clothing storage': { display: 'Clothing Storage', category: 'Bedroom' },
  'blackout blinds': { display: 'Blackout Blinds', category: 'Bedroom' },
  'blackout curtains': { display: 'Blackout Curtains', category: 'Bedroom' },
  
  // Heating & Cooling
  'air conditioning': { display: 'Air Conditioning', category: 'Heating & Cooling' },
  'ac': { display: 'Air Conditioning', category: 'Heating & Cooling' },
  'heating': { display: 'Heating', category: 'Heating & Cooling' },
  'heat pump': { display: 'Heat Pump', category: 'Heating & Cooling' },
  'fireplace': { display: 'Fireplace', category: 'Heating & Cooling' },
  'gas fireplace': { display: 'Gas Fireplace', category: 'Heating & Cooling' },
  'indoor fireplace': { display: 'Indoor Fireplace', category: 'Heating & Cooling' },
  
  // Internet & Office
  'free wifi': { display: 'Free WiFi', category: 'Internet & Office' },
  'wifi': { display: 'WiFi', category: 'Internet & Office' },
  'high speed wifi': { display: 'High-Speed WiFi', category: 'Internet & Office' },
  'highspeed wifi': { display: 'High-Speed WiFi', category: 'Internet & Office' },
  'internet': { display: 'Internet', category: 'Internet & Office' },
  'dedicated workspace': { display: 'Dedicated Workspace', category: 'Internet & Office' },
  'workspace': { display: 'Workspace', category: 'Internet & Office' },
  'office': { display: 'Office', category: 'Internet & Office' },
  'desk': { display: 'Desk', category: 'Internet & Office' },
  
  // Entertainment
  'television': { display: 'Television', category: 'Entertainment' },
  'tv': { display: 'Television', category: 'Entertainment' },
  'smart tv': { display: 'Smart TV', category: 'Entertainment' },
  'cable tv': { display: 'Cable TV', category: 'Entertainment' },
  'netflix': { display: 'Netflix', category: 'Entertainment' },
  'games': { display: 'Games', category: 'Entertainment' },
  'board games': { display: 'Board Games', category: 'Entertainment' },
  'golf balls and clubs': { display: 'Golf Balls & Clubs', category: 'Entertainment' },
  'living room': { display: 'Living Room', category: 'Entertainment' },
  'living area': { display: 'Living Area', category: 'Entertainment' },
  'ping pong table': { display: 'Ping Pong Table', category: 'Entertainment' },
  'table tennis': { display: 'Table Tennis', category: 'Entertainment' },
  
  // Outdoor Features & Facilities
  'hot tub': { display: 'Hot Tub', category: 'Outdoor Features & Facilities' },
  'cedar spa pools': { display: 'Cedar Spa Pools', category: 'Outdoor Features & Facilities' },
  'spa': { display: 'Spa', category: 'Outdoor Features & Facilities' },
  'kayak canoe': { display: 'Kayak / Canoe', category: 'Outdoor Features & Facilities' },
  'kayak and canoe': { display: 'Kayak / Canoe', category: 'Outdoor Features & Facilities' },
  'kayak': { display: 'Kayak', category: 'Outdoor Features & Facilities' },
  'canoe': { display: 'Canoe', category: 'Outdoor Features & Facilities' },
  'deck patio': { display: 'Deck / Patio', category: 'Outdoor Features & Facilities' },
  'deck and patio': { display: 'Deck / Patio', category: 'Outdoor Features & Facilities' },
  'deck': { display: 'Deck', category: 'Outdoor Features & Facilities' },
  'patio': { display: 'Patio', category: 'Outdoor Features & Facilities' },
  'lanai gazebo': { display: 'Lanai / Gazebo', category: 'Outdoor Features & Facilities' },
  'lanai and gazebo': { display: 'Lanai / Gazebo', category: 'Outdoor Features & Facilities' },
  'lanai': { display: 'Lanai', category: 'Outdoor Features & Facilities' },
  'gazebo': { display: 'Gazebo', category: 'Outdoor Features & Facilities' },
  'lawn garden': { display: 'Lawn / Garden', category: 'Outdoor Features & Facilities' },
  'lawn and garden': { display: 'Lawn / Garden', category: 'Outdoor Features & Facilities' },
  'lawn': { display: 'Lawn', category: 'Outdoor Features & Facilities' },
  'garden': { display: 'Garden', category: 'Outdoor Features & Facilities' },
  'outdoor grill': { display: 'Outdoor BBQ', category: 'Kitchen & Dining' },
  'bbq': { display: 'Outdoor BBQ', category: 'Kitchen & Dining' },
  'barbecue': { display: 'Outdoor BBQ', category: 'Kitchen & Dining' },
  'grill': { display: 'Outdoor BBQ', category: 'Kitchen & Dining' },
  'outdoor bbq': { display: 'Outdoor BBQ', category: 'Kitchen & Dining' },
  'outdoor furniture': { display: 'Outdoor Furniture', category: 'Outdoor Features & Facilities' },
  'outdoor seating': { display: 'Outdoor Seating', category: 'Outdoor Features & Facilities' },
  'private patio or deck': { display: 'Private Patio or Deck', category: 'Outdoor Features & Facilities' },
  'private patio': { display: 'Private Patio or Deck', category: 'Outdoor Features & Facilities' },
  'private deck': { display: 'Private Patio or Deck', category: 'Outdoor Features & Facilities' },
  'balcony': { display: 'Balcony', category: 'Outdoor Features & Facilities' },
  'terrace': { display: 'Terrace', category: 'Outdoor Features & Facilities' },
  'pool': { display: 'Pool', category: 'Outdoor Features & Facilities' },
  'swimming pool': { display: 'Swimming Pool', category: 'Outdoor Features & Facilities' },
  'beach': { display: 'Beach', category: 'Outdoor Features & Facilities' },
  'beach access': { display: 'Beach Access', category: 'Outdoor Features & Facilities' },
  'private beach': { display: 'Private Beach', category: 'Outdoor Features & Facilities' },
  '100m of private beach': { display: '100m of Private Beach', category: 'Outdoor Features & Facilities' },
  'lake view': { display: 'Lake View', category: 'Outdoor Features & Facilities' },
  'lake rotorua views': { display: 'Lake Rotorua Views', category: 'Outdoor Features & Facilities' },
  'floatplane': { display: 'Floatplane Transfers', category: 'Outdoor Features & Facilities' },
  'float plane': { display: 'Floatplane Transfers', category: 'Outdoor Features & Facilities' },
  
  // Parking & Access
  'parking': { display: 'Parking', category: 'Parking & Access' },
  'free parking': { display: 'Free Parking', category: 'Parking & Access' },
    'garage': { display: 'Garage', category: 'Parking & Access' },
  'covered parking': { display: 'Covered Parking', category: 'Parking & Access' },
  'covered parking and storage': { display: 'Covered Parking & Storage', category: 'Parking & Access' },
  'private parking': { display: 'Private Parking', category: 'Parking & Access' },
  'ev charging': { display: 'EV Charging', category: 'Parking & Access' },
  
  // Family
  'highchair': { display: 'Highchair', category: 'Family' },
  'high chair': { display: 'Highchair', category: 'Family' },
  'cot': { display: 'Cot', category: 'Family' },
  'crib': { display: 'Crib', category: 'Family' },
  'baby friendly': { display: 'Baby Friendly', category: 'Family' },
  'child safe': { display: 'Child Safe', category: 'Family' },
  
  // Health & Safety
  'smoke detector': { display: 'Smoke Detector', category: 'Health & Safety' },
  'smoke alarm': { display: 'Smoke Alarm', category: 'Health & Safety' },
  'fire extinguisher': { display: 'Fire Extinguisher', category: 'Health & Safety' },
  'first aid kit': { display: 'First Aid Kit', category: 'Health & Safety' },
  'cleaned with disinfectant': { display: 'Cleaned with Disinfectant', category: 'Health & Safety' },
  'security': { display: 'Security', category: 'Health & Safety' },
  'safe': { display: 'Safe', category: 'Health & Safety' },
  'security camera': { display: 'Security Cameras (Exterior Only)', category: 'Health & Safety' },
  'security cameras': { display: 'Security Cameras (Exterior Only)', category: 'Health & Safety' },
  'security cameras exterior only': { display: 'Security Cameras (Exterior Only)', category: 'Health & Safety' },
  
  // Check-in
  'self checkin': { display: 'Self Check-in', category: 'Check-in' },
  'self check in': { display: 'Self Check-in', category: 'Check-in' },
  'door code entry': { display: 'Door Code Entry', category: 'Check-in' },
  'keyless entry': { display: 'Door Code Entry', category: 'Check-in' },
  'keypad': { display: 'Door Code Entry', category: 'Check-in' },
  'keypad entry': { display: 'Door Code Entry', category: 'Check-in' },
  'digital lock': { display: 'Door Code Entry', category: 'Check-in' },
  'private entrance': { display: 'Private Entrance', category: 'Check-in' },
};

// Category display order
const categoryOrder = [
  'Kitchen & Dining',
  'Bathroom & Laundry',
  'Bedroom',
  'Heating & Cooling',
  'Internet & Office',
  'Entertainment',
  'Outdoor Features & Facilities',
  'Parking & Access',
  'Family',
  'Health & Safety',
  'Check-in'
];

// Manually added amenities that ALL villas have but aren't in Uplisting API
// These will be added to every property's amenities list
const manualAmenitiesForAllVillas: string[] = [
  // Family
  'Cot',
  'Highchair',
  // Internet & Office
  'Dedicated Workspace',
  // Kitchen & Dining
  'Hot Water Kettle',
  'Baking Sheet',
  'Dining Table',
  'Wine Glasses',
  // Bathroom & Laundry
  'Body Soap, Shampoo & Conditioner',
  'Cleaning Products',
  // Bedroom
  'Blackout Curtains',
  'Clothing Storage',
  'Hangers',
  'Extra Pillows & Blankets',
  // Outdoor Features & Facilities
  'Outdoor Furniture',
  'Private Patio or Deck',
  // Health & Safety
  'Security Cameras (Exterior Only)',
  'First Aid Kit',
  'Cleaned with Disinfectant',
  // Check-in
  'Self Check-in',
  'Door Code Entry',
  'Private Entrance'
];

// Amenities that only apply to Toka Ridge lakefront properties (not Above Par)
const tokaRidgeLakefrontOnlyAmenities: string[] = [
  'Floatplane Transfers'
];

// Amenities that only apply to Above Par property
const aboveParOnlyAmenities: string[] = [
  'Golf Balls & Clubs'
];

// Categorize a list of amenities from Uplisting API
// Optional propertyName parameter to add property-specific amenities
export function categorizeAmenities(propertyAmenities: string[], propertyName?: string): AmenityCategory[] {
  const categoryMap = new Map<string, Set<string>>();
  const unmappedAmenities = new Set<string>();
  
  // Initialize all categories
  categoryOrder.forEach(cat => categoryMap.set(cat, new Set()));
  
  // Combine Uplisting amenities with manually added ones
  let allAmenities = [...propertyAmenities, ...manualAmenitiesForAllVillas];
  
  // Add Toka Ridge lakefront-only amenities (not for Above Par)
  if (propertyName && !propertyName.includes('Above Par')) {
    allAmenities = [...allAmenities, ...tokaRidgeLakefrontOnlyAmenities];
  }
  
  // Add Above Par-only amenities
  if (propertyName && propertyName.includes('Above Par')) {
    allAmenities = [...allAmenities, ...aboveParOnlyAmenities];
  }
  
  // Process each amenity
  allAmenities.forEach(amenity => {
    const normalized = normalizeAmenity(amenity);
    const mapping = amenityMapping[normalized];
    
    if (mapping) {
      const categorySet = categoryMap.get(mapping.category);
      if (categorySet) {
        categorySet.add(mapping.display);
      }
    } else {
      // Add to "Other" category so nothing is lost
      unmappedAmenities.add(amenity);
    }
  });
  
  // Convert to array format, filtering out empty categories
  // Note: Unmapped amenities are silently ignored (no "Other" category)
  return categoryOrder
    .map(categoryName => ({
      name: categoryName,
      amenities: Array.from(categoryMap.get(categoryName) || [])
    }))
    .filter(category => category.amenities.length > 0);
}

// Legacy exports for backwards compatibility
export const categorizedAmenities: AmenityCategory[] = categoryOrder.map(name => ({
  name,
  amenities: Object.entries(amenityMapping)
    .filter(([_, value]) => value.category === name)
    .map(([_, value]) => value.display)
    .filter((v, i, a) => a.indexOf(v) === i) // unique values
}));

export const allPropertyAmenities: string[] = Object.values(amenityMapping).map(m => m.display);

export function getAmenityCategory(amenity: string): string | null {
  const normalized = normalizeAmenity(amenity);
  return amenityMapping[normalized]?.category || null;
}

export function isKnownAmenity(amenity: string): boolean {
  const normalized = normalizeAmenity(amenity);
  return normalized in amenityMapping;
}
